import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, Calendar, Heart, CheckCircle2, ShieldCheck, Clock, CreditCard } from 'lucide-react';
import { fallbackYdelser, contactInfo, sortServices } from '../data';

export default function ServiceDetail({ scrollToSection }) {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [allServices, setAllServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchServiceData = async () => {
      try {
        const response = await fetch('https://api.dev.auxo.dk/public/content/ydelser');
        let servicesData = [];
        if (response.ok) {
          const data = await response.json();
          if (data && data.length > 0) {
            servicesData = sortServices(data);
          } else {
            servicesData = sortServices(fallbackYdelser);
          }
        } else {
          servicesData = sortServices(fallbackYdelser);
        }

        setAllServices(servicesData);
        const found = servicesData.find(s => (s.ydelse_slug?.da || s.ydelse_slug || s.id) === slug);
        setService(found || null);
      } catch (error) {
        console.error('Error fetching service detail:', error);
        const sortedFallback = sortServices(fallbackYdelser);
        setAllServices(sortedFallback);
        const found = sortedFallback.find(s => (s.ydelse_slug?.da || s.ydelse_slug || s.id) === slug);
        setService(found || null);
      } finally {
        setLoading(false);
      }
    };

    fetchServiceData();
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    if (service) {
      const serviceTitle = service.titel?.da || service.titel || '';
      const serviceShortDesc = service.kort_beskrivelse?.da || service.kort_beskrivelse || '';
      
      document.title = `${serviceTitle} | Psykolog Mette Brisson – Frederiksberg`;
      
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        // Fallback to a clean description if short description is missing
        const descText = serviceShortDesc || `Læs mere om ${serviceTitle} hos Psykolog Mette Brisson på Frederiksberg. Vi tilbyder professionel terapi tilpasset dine behov.`;
        metaDescription.setAttribute('content', descText);
      }
    }
  }, [service]);

  if (loading) {
    return (
      <div className="min-h-screen flex justify-center items-center bg-[#F7F4F1]">
        <div className="animate-pulse flex flex-col items-center gap-4">
          <div className="w-10 h-10 rounded-full border-2 border-t-[#BFC8BE] border-r-transparent border-b-transparent border-l-transparent animate-spin" />
          <p className="font-sans-clean text-xs tracking-widest uppercase text-[#4A4A4A]/50">Henter detaljer...</p>
        </div>
      </div>
    );
  }

  if (!service) {
    return (
      <div className="min-h-screen flex flex-col justify-center items-center bg-[#F7F4F1] px-6 text-center">
        <h2 className="font-serif-elegant text-3xl text-[#4A4A4A] mb-4">Ydelsen blev ikke fundet</h2>
        <p className="font-sans-clean text-sm text-[#4A4A4A]/70 mb-8 max-w-md">
          Den ydelse, du leder efter, findes desværre ikke eller er blevet flyttet.
        </p>
        <Link
          to="/"
          className="px-6 py-3 bg-[#BFC8BE] text-white font-sans-clean text-xs tracking-widest uppercase rounded-sm hover:bg-[#BFC8BE]/90 transition-all duration-300"
        >
          Tilbage til forsiden
        </Link>
      </div>
    );
  }

  const title = service.titel?.da || service.titel || '';
  const descHtml = service.beskrivelse?.da || service.beskrivelse || '';
  const image = service.billede?.[0] || 'https://assets.auxodev.dk/assets/4291744f-0a58-4691-8a7f-39ecbae78b95/6fe25267-fac3-4f86-ad2f-975d9bdf41da.jpg';

  const getServicePrice = () => {
    const lowerTitle = title.toLowerCase();
    if (lowerTitle.includes('parterapi')) {
      return contactInfo.pricing.couple;
    }
    if (lowerTitle.includes('familieterapi')) {
      return contactInfo.pricing.family;
    }
    if (lowerTitle.includes('supervision')) {
      return contactInfo.pricing.supervision;
    }
    return contactInfo.pricing.individual;
  };

  const getServicePriceLabel = () => {
    const lowerTitle = title.toLowerCase();
    if (lowerTitle.includes('parterapi')) {
      return 'Parterapi:';
    }
    if (lowerTitle.includes('familieterapi')) {
      return 'Familieterapi:';
    }
    if (lowerTitle.includes('supervision')) {
      return 'Supervision:';
    }
    return 'Individuel samtale:';
  };

  // Filter out current service to show other suggestions
  const otherServices = allServices
    .filter(s => (s.ydelse_slug?.da || s.ydelse_slug || s.id) !== slug)
    .slice(0, 2);

  const handleBookClick = () => {
    scrollToSection('kontakt');
  };

  return (
    <div className="pt-28 pb-16 md:pb-24 bg-[#F7F4F1]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        
        {/* Breadcrumbs */}
        <nav className="flex items-center gap-2 text-xs tracking-wider uppercase text-[#4A4A4A]/50 mb-8 md:mb-12 font-sans-clean">
          <Link to="/" className="hover:text-[#BFC8BE] transition-colors">Forside</Link>
          <span>/</span>
          <span className="hover:text-[#BFC8BE] cursor-pointer" onClick={() => scrollToSection('ydelser')}>Ydelser</span>
          <span>/</span>
          <span className="text-[#4A4A4A] font-medium truncate max-w-[200px] sm:max-w-none">{title}</span>
        </nav>

        {/* Main Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          
          {/* Left Column: Image & Info Cards */}
          <div className="lg:col-span-5 space-y-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="relative rounded-2xl overflow-hidden shadow-md border border-[#EDE6DF]/40"
            >
              <img
                src={image}
                alt={title}
                className="w-full h-[300px] sm:h-[400px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent pointer-events-none" />
            </motion.div>

            {/* Practical Info Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-white p-6 md:p-8 rounded-2xl border border-[#EDE6DF]/40 shadow-sm space-y-6 text-left"
            >
              <h4 className="font-serif-elegant text-lg text-[#4A4A4A] font-semibold flex items-center gap-2">
                <ShieldCheck size={20} className="text-[#BFC8BE]" />
                Praktisk information
              </h4>
              <div className="w-8 h-[1px] bg-[#BFC8BE]" />
              
              <ul className="space-y-4 font-sans-clean text-sm text-[#4A4A4A]/80">
                <li className="flex items-start gap-3">
                  <CheckCircle2 size={16} className="text-[#BFC8BE] shrink-0 mt-0.5" />
                  <div>
                    <span className="font-semibold text-[#4A4A4A]">{getServicePriceLabel()}</span>
                    <p className="text-xs text-[#4A4A4A]/70 mt-0.5">{getServicePrice()}</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 size={16} className="text-[#BFC8BE] shrink-0 mt-0.5" />
                  <div>
                    <span className="font-semibold text-[#4A4A4A]">Klinik på Frederiksberg:</span>
                    <p className="text-xs text-[#4A4A4A]/70 mt-0.5">{contactInfo.address}, {contactInfo.zipCity}</p>
                  </div>
                </li>
                <li className="flex items-start gap-3 border-t border-[#EDE6DF]/40 pt-4">
                  <Clock size={16} className="text-[#BFC8BE] shrink-0 mt-0.5" />
                  <div>
                    <span className="font-semibold text-[#4A4A4A]">Afbudsregler:</span>
                    <p className="text-xs text-[#4A4A4A]/70 mt-1 leading-relaxed">
                      Afbud meldes senest dagen før inden kl. 12 på mail, sms eller telefon. Herefter afregnes fuld pris for, for sent afbud eller udeblivelse.
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-3 border-t border-[#EDE6DF]/40 pt-4">
                  <CreditCard size={16} className="text-[#BFC8BE] shrink-0 mt-0.5" />
                  <div>
                    <span className="font-semibold text-[#4A4A4A]">Betaling:</span>
                    <p className="text-xs text-[#4A4A4A]/70 mt-1 leading-relaxed">
                      Betaling sker via fremsendt faktura. Betalingsfrist er 5 dage fra udstedelsesdato og betalingen overføres til bankkonto i Lån og Spar Bank. Kontonummeret vil fremgå af fakturaen og du bedes anføre fakturanummeret på overførslen.
                    </p>
                  </div>
                </li>
              </ul>
            </motion.div>
          </div>

          {/* Right Column: Detailed Text */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="lg:col-span-7 text-left"
          >
            <h1 className="font-serif-elegant text-3xl md:text-4xl lg:text-5xl text-[#4A4A4A] font-light mb-6 leading-tight">
              {title}
            </h1>

            <div className="w-12 h-[1px] bg-[#BFC8BE] mb-8" />

            {/* Rich Text Description */}
            <div
              className="font-sans-clean text-sm md:text-base text-[#4A4A4A]/80 leading-relaxed space-y-6 mb-10 prose prose-neutral max-w-none"
              dangerouslySetInnerHTML={{ __html: descHtml }}
            />

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 border-t border-[#EDE6DF] pt-8">
              <button
                onClick={handleBookClick}
                className="px-6 py-3.5 bg-[#BFC8BE] hover:bg-[#BFC8BE]/90 text-white font-sans-clean text-xs tracking-widest uppercase rounded-sm transition-all duration-300 shadow-sm hover:shadow-md flex items-center justify-center gap-2 group cursor-pointer"
              >
                Book samtale nu
                <ArrowRight size={12} className="transition-transform duration-300 group-hover:translate-x-0.5" />
              </button>
              <Link
                to="/"
                className="px-6 py-3.5 border border-[#4A4A4A]/20 hover:border-[#4A4A4A]/40 text-[#4A4A4A] font-sans-clean text-xs tracking-widest uppercase rounded-sm transition-all duration-300 bg-transparent text-center"
              >
                Gå tilbage til forsiden
              </Link>
            </div>
          </motion.div>

        </div>

        {/* Other Services Section */}
        {otherServices.length > 0 && (
          <div className="mt-20 md:mt-28 border-t border-[#EDE6DF] pt-16">
            <div className="text-center mb-12">
              <h3 className="font-serif-elegant text-2xl md:text-3xl text-[#4A4A4A] font-light mb-4">
                Andre områder jeg kan hjælpe dig med
              </h3>
              <div className="w-12 h-[1px] bg-[#BFC8BE] mx-auto" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {otherServices.map((other, idx) => {
                const oTitle = other.titel?.da || other.titel || '';
                const oShortDesc = other.kort_beskrivelse?.da || other.kort_beskrivelse || '';
                const oSlug = other.ydelse_slug?.da || other.ydelse_slug || other.id;
                const oImage = other.billede?.[0] || 'https://assets.auxodev.dk/assets/4291744f-0a58-4691-8a7f-39ecbae78b95/6fe25267-fac3-4f86-ad2f-975d9bdf41da.jpg';

                return (
                  <Link
                    key={other.id || idx}
                    to={`/ydelse/${oSlug}`}
                    className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 border border-[#EDE6DF]/40 flex flex-col text-left group"
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={oImage}
                        alt={oTitle}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-102"
                      />
                    </div>
                    <div className="p-6 flex flex-col flex-grow">
                      <h4 className="font-serif-elegant text-xl text-[#4A4A4A] font-light mb-2 group-hover:text-[#BFC8BE] transition-colors duration-300">
                        {oTitle}
                      </h4>
                      <p className="font-sans-clean text-xs text-[#4A4A4A]/70 leading-relaxed mb-4 flex-grow line-clamp-2">
                        {oShortDesc}
                      </p>
                      <span className="inline-flex items-center gap-1 text-[10px] tracking-widest uppercase text-[#4A4A4A] font-semibold group-hover:text-[#BFC8BE] transition-colors duration-300">
                        Læs mere
                        <ArrowRight size={10} className="transition-transform duration-300 group-hover:translate-x-0.5" />
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
